import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';

const PRODUCT_TABS = ['Visa', 'Ticket', 'Package', 'PackageTiers'];
const COMM_TYPES = [
  { value: 'GP_TIERED',   label: 'GP Tiered (LKR)',   hint: 'Company earns X LKR GP → agent gets Y LKR' },
  { value: 'COUNT_BASED', label: 'Count Based',        hint: 'e.g. 100 visas processed → LKR 150 per visa' },
  { value: 'PERCENT',     label: 'Percentage',         hint: '% of GP / Sell Price / Cost' },
  { value: 'FIXED',       label: 'Fixed per Transaction', hint: 'Flat LKR amount every time' },
];

const blankTier = (ct) => ({
  gp_from: '', gp_to: '',
  fixed_amount: '', commission_percent: '', amount_per_unit: '',
  commission_basis: 'ON_GP', commission_type: ct,
});

export default function CommissionTable() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'system';
  const [tab, setTab] = useState('Visa');
  const [blocks, setBlocks] = useState([]);
  const [pkgTiers, setPkgTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New block form (shown inline at top when active)
  const [newBlock, setNewBlock] = useState(null); // null | { rule_name, commission_type, notes }
  const [pendingTiers, setPendingTiers] = useState([]); // tiers accumulated before saving
  const [newTierForm, setNewTierForm] = useState(null); // current tier being entered in new block form
  const [savingNew, setSavingNew] = useState(false);

  // Inline tier adding — keyed by rule_name (for existing blocks)
  const [addingTier, setAddingTier] = useState({}); // { [rule_name]: tierFormState }
  const [savingTier, setSavingTier] = useState({});

  // Inline tier editing — keyed by tier.id
  const [editingTier, setEditingTier] = useState({}); // { [tier.id]: tierFormState }
  const [savingEditTier, setSavingEditTier] = useState({});

  // Block rename
  const [renamingBlock, setRenamingBlock] = useState({}); // { [rule_name]: newName }

  const load = useCallback(async () => {
    setLoading(true); setError('');
    if (tab === 'PackageTiers') {
      const { data } = await supabase.from('package_commission_tiers').select('*, packages(id, package_name, package_code)').order('gp_from');
      const map = {};
      (data || []).forEach(t => {
        if (!t.packages) return;
        const k = t.packages.id;
        if (!map[k]) map[k] = { pkg: t.packages, tiers: [] };
        map[k].tiers.push(t);
      });
      setPkgTiers(Object.values(map));
    } else {
      const { data, error: err } = await supabase.from('commission_tables').select('*').eq('product_type', tab).order('rule_name').order('gp_from');
      if (err) { setError(err.message); setLoading(false); return; }
      const map = {};
      (data || []).forEach(r => {
        const k = r.rule_name || '(Unnamed)';
        if (!map[k]) map[k] = { rule_name: k, product_type: r.product_type, commission_type: r.commission_type || 'GP_TIERED', notes: r.notes, tiers: [] };
        map[k].tiers.push(r);
      });
      setBlocks(Object.values(map));
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); setNewBlock(null); setPendingTiers([]); setNewTierForm(null); setAddingTier({}); }, [load]);

  // ---- Tier save ----
  const saveTier = async (block) => {
    const tf = addingTier[block.rule_name];
    if (!tf) return;
    setSavingTier(s => ({ ...s, [block.rule_name]: true }));
    setError('');
    const payload = {
      product_type: tab,
      rule_name: block.rule_name,
      commission_type: block.commission_type,
      notes: block.notes || null,
      gp_from: parseFloat(tf.gp_from) || 0,
      gp_to: tf.gp_to !== '' && tf.gp_to != null ? parseFloat(tf.gp_to) : null,
      commission_percent: parseFloat(tf.commission_percent) || 0,
      fixed_amount: tf.fixed_amount !== '' ? parseFloat(tf.fixed_amount) : null,
      amount_per_unit: tf.amount_per_unit !== '' ? parseFloat(tf.amount_per_unit) : null,
      commission_basis: tf.commission_basis || 'ON_GP',
      is_active: true,
      created_by: username,
    };
    const { error: err } = await supabase.from('commission_tables').insert(payload);
    setSavingTier(s => ({ ...s, [block.rule_name]: false }));
    if (err) { setError(err.message); return; }
    // Reset tier form for this block (stay open to add more)
    setAddingTier(s => ({ ...s, [block.rule_name]: blankTier(block.commission_type) }));
    load();
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    await supabase.from('commission_tables').delete().eq('id', id);
    load();
  };

  const deleteBlock = async (rule_name) => {
    if (!window.confirm(`Delete entire "${rule_name}" block and all its tiers?`)) return;
    await supabase.from('commission_tables').delete().eq('rule_name', rule_name).eq('product_type', tab);
    load();
  };

  const startEditTier = (tier, ct) => {
    setEditingTier(s => ({ ...s, [tier.id]: {
      gp_from: tier.gp_from ?? '',
      gp_to: tier.gp_to ?? '',
      fixed_amount: tier.fixed_amount ?? '',
      commission_percent: tier.commission_percent ?? '',
      amount_per_unit: tier.amount_per_unit ?? '',
      commission_basis: tier.commission_basis || 'ON_GP',
      commission_type: ct,
    }}));
  };

  const saveEditTier = async (tier, block) => {
    const tf = editingTier[tier.id];
    if (!tf) return;
    setSavingEditTier(s => ({ ...s, [tier.id]: true }));
    const { error: err } = await supabase.from('commission_tables').update({
      gp_from: parseFloat(tf.gp_from) || 0,
      gp_to: tf.gp_to !== '' && tf.gp_to != null ? parseFloat(tf.gp_to) : null,
      commission_percent: parseFloat(tf.commission_percent) || 0,
      fixed_amount: tf.fixed_amount !== '' ? parseFloat(tf.fixed_amount) : null,
      amount_per_unit: tf.amount_per_unit !== '' ? parseFloat(tf.amount_per_unit) : null,
      commission_basis: tf.commission_basis || 'ON_GP',
    }).eq('id', tier.id);
    setSavingEditTier(s => ({ ...s, [tier.id]: false }));
    if (err) { setError(err.message); return; }
    setEditingTier(s => { const n = { ...s }; delete n[tier.id]; return n; });
    load();
  };

  const saveBlockRename = async (oldName, newName) => {
    if (!newName.trim() || newName.trim() === oldName) { setRenamingBlock(s => { const n = { ...s }; delete n[oldName]; return n; }); return; }
    await supabase.from('commission_tables').update({ rule_name: newName.trim() }).eq('rule_name', oldName).eq('product_type', tab);
    setRenamingBlock(s => { const n = { ...s }; delete n[oldName]; return n; });
    load();
  };

  // ---- New block: accumulate a tier into pendingTiers ----
  const addPendingTier = () => {
    if (!newTierForm) return;
    const tf = newTierForm;
    setPendingTiers(pts => [...pts, {
      gp_from:            parseFloat(tf.gp_from) || 0,
      gp_to:              tf.gp_to !== '' && tf.gp_to != null ? parseFloat(tf.gp_to) : null,
      commission_percent: parseFloat(tf.commission_percent) || 0,
      fixed_amount:       tf.fixed_amount !== '' ? parseFloat(tf.fixed_amount) : null,
      amount_per_unit:    tf.amount_per_unit !== '' ? parseFloat(tf.amount_per_unit) : null,
      commission_basis:   tf.commission_basis || 'ON_GP',
    }]);
    setNewTierForm(blankTier(newBlock.commission_type));
  };

  const removePendingTier = (idx) => setPendingTiers(pts => pts.filter((_, i) => i !== idx));

  // ---- New block: save all pending tiers at once ----
  const createBlock = async () => {
    if (!newBlock?.rule_name?.trim() || pendingTiers.length === 0) return;
    setSavingNew(true); setError('');
    const rows = pendingTiers.map(tf => ({
      product_type:       tab,
      rule_name:          newBlock.rule_name.trim(),
      commission_type:    newBlock.commission_type,
      notes:              newBlock.notes || null,
      gp_from:            tf.gp_from,
      gp_to:              tf.gp_to,
      commission_percent: tf.commission_percent,
      fixed_amount:       tf.fixed_amount,
      amount_per_unit:    tf.amount_per_unit,
      commission_basis:   tf.commission_basis,
      is_active:          true,
      created_by:         username,
    }));
    const { error: err } = await supabase.from('commission_tables').insert(rows);
    setSavingNew(false);
    if (err) { setError(err.message); return; }
    setNewBlock(null); setPendingTiers([]); setNewTierForm(null);
    load();
  };

  // ---- Tier form renderer ----
  // NOTE: this is a plain render function (called inline), NOT a component mounted
  // as <TierFields/>. Defining a component inside render gives it a new identity on
  // every keystroke, which remounts the inputs and steals focus after one character.
  const renderTierFields = (ct, tf, onChange) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
      {(ct === 'GP_TIERED' || ct === 'PERCENT') && <>
        <div style={{ flex: '1 1 120px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>{ct === 'PERCENT' ? 'GP% From' : 'GP From (LKR)'}</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.gp_from} onChange={e => onChange('gp_from', e.target.value)} placeholder="0" />
        </div>
        <div style={{ flex: '1 1 120px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>{ct === 'PERCENT' ? 'GP% To (blank = ∞)' : 'GP To (LKR, blank = ∞)'}</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.gp_to} onChange={e => onChange('gp_to', e.target.value)} placeholder="blank = and above" />
        </div>
      </>}
      {ct === 'COUNT_BASED' && <>
        <div style={{ flex: '1 1 120px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Count From</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.gp_from} onChange={e => onChange('gp_from', e.target.value)} placeholder="e.g. 1" />
        </div>
        <div style={{ flex: '1 1 120px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Count To (blank = ∞)</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.gp_to} onChange={e => onChange('gp_to', e.target.value)} placeholder="optional" />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>LKR per Unit</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.amount_per_unit} onChange={e => onChange('amount_per_unit', e.target.value)} placeholder="e.g. 150" />
        </div>
      </>}
      {ct === 'GP_TIERED' && (
        <div style={{ flex: '1 1 140px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Agent Commission (LKR)</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.fixed_amount} onChange={e => onChange('fixed_amount', e.target.value)} placeholder="e.g. 500" />
        </div>
      )}
      {ct === 'PERCENT' && <>
        <div style={{ flex: '1 1 100px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Commission %</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.commission_percent} onChange={e => onChange('commission_percent', e.target.value)} placeholder="e.g. 10" />
        </div>
        <div style={{ flex: '1 1 110px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Basis</label>
          <select className="form-input" value={tf.commission_basis} onChange={e => onChange('commission_basis', e.target.value)}>
            <option value="ON_GP">On GP</option>
            <option value="ON_SELL">On Sell</option>
            <option value="ON_COST">On Cost</option>
          </select>
        </div>
      </>}
      {ct === 'FIXED' && (
        <div style={{ flex: '1 1 180px' }}>
          <label className="form-label" style={{ fontSize: 11 }}>Fixed Amount per Transaction (LKR)</label>
          <input className="form-input" type="text" inputMode="decimal" value={tf.fixed_amount} onChange={e => onChange('fixed_amount', e.target.value)} placeholder="e.g. 1000" />
        </div>
      )}
    </div>
  );

  const tierDesc = (tier, ct) => {
    if (ct === 'GP_TIERED')   return `GP ≥ LKR ${parseFloat(tier.gp_from).toLocaleString()}${tier.gp_to != null ? ` → < LKR ${parseFloat(tier.gp_to).toLocaleString()}` : '+'} — Agent gets LKR ${parseFloat(tier.fixed_amount || 0).toLocaleString()}`;
    if (ct === 'COUNT_BASED') return `${parseFloat(tier.gp_from).toLocaleString()}${tier.gp_to != null ? `–${parseFloat(tier.gp_to).toLocaleString()}` : '+'} units — LKR ${parseFloat(tier.amount_per_unit || 0).toLocaleString()} per unit`;
    if (ct === 'PERCENT')     return `GP ${parseFloat(tier.gp_from).toFixed(1)}%${tier.gp_to != null ? `–${parseFloat(tier.gp_to).toFixed(1)}%` : '+'} — ${parseFloat(tier.commission_percent || 0).toFixed(1)}% of ${tier.commission_basis}`;
    if (ct === 'FIXED')       return `LKR ${parseFloat(tier.fixed_amount || 0).toLocaleString()} per transaction`;
    return '—';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Commission Rules</div>
          <div className="page-subtitle">Named commission blocks — each block has its own tier structure</div>
        </div>
        {tab !== 'PackageTiers' && !newBlock && (
          <button className="btn btn-primary" onClick={() => {
            setNewBlock({ rule_name: '', commission_type: 'GP_TIERED', notes: '' });
            setPendingTiers([]);
            setNewTierForm(blankTier('GP_TIERED'));
          }}>+ New Commission Block</button>
        )}
      </div>

      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {PRODUCT_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 22px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t ? 700 : 400,
            color: tab === t ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, fontSize: 14,
          }}>{t === 'PackageTiers' ? 'Per-Package Tiers' : t + 's'}</button>
        ))}
      </div>

      {/* New block form */}
      {newBlock && (
        <div className="card" style={{ marginBottom: 20, border: '2px solid var(--primary)', background: 'rgba(79,110,247,0.03)' }}>
          <div className="card-title" style={{ marginBottom: 16 }}>New Commission Block</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ flex: '2 1 200px' }}>
              <label className="form-label">Block Name *</label>
              <input className="form-input" autoFocus value={newBlock.rule_name} onChange={e => setNewBlock(b => ({ ...b, rule_name: e.target.value }))} placeholder="e.g. China Visa, Emirates Ticket" />
            </div>
            <div style={{ flex: '2 1 200px' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={newBlock.notes} onChange={e => setNewBlock(b => ({ ...b, notes: e.target.value }))} placeholder="optional description" />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Commission Type</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
              {COMM_TYPES.map(ct => (
                <label key={ct.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '8px 14px', borderRadius: 7, border: `1px solid ${newBlock.commission_type === ct.value ? 'var(--primary)' : 'var(--border)'}`, background: newBlock.commission_type === ct.value ? 'rgba(79,110,247,0.07)' : 'white' }}>
                  <input type="radio" checked={newBlock.commission_type === ct.value} onChange={() => {
                    setNewBlock(b => ({ ...b, commission_type: ct.value }));
                    setPendingTiers([]);
                    setNewTierForm(blankTier(ct.value));
                  }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{ct.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ct.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {/* Accumulated tiers */}
          {pendingTiers.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>Tiers ({pendingTiers.length})</div>
              {pendingTiers.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 6, background: 'var(--bg)', marginBottom: 4, fontSize: 13 }}>
                  <span>{tierDesc(t, newBlock.commission_type)}</span>
                  <button onClick={() => removePendingTier(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* Current tier form */}
          {newTierForm && (
            <div style={{ background: 'var(--bg)', borderRadius: 7, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase' }}>Add Tier</div>
              {renderTierFields(newBlock.commission_type, newTierForm, (k, v) => setNewTierForm(f => ({ ...f, [k]: v })))}
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-secondary btn-sm" onClick={addPendingTier}>+ Add Tier to List</button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 10 }}>add all tiers, then click Create Block below</span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => { setNewBlock(null); setPendingTiers([]); setNewTierForm(null); }}>Cancel</button>
            <button className="btn btn-primary" disabled={!newBlock.rule_name.trim() || pendingTiers.length === 0 || savingNew} onClick={createBlock}>
              {savingNew ? 'Saving…' : `Create Block (${pendingTiers.length} tier${pendingTiers.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="spinner" /> : tab === 'PackageTiers' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {pkgTiers.map(({ pkg, tiers }) => (
            <div className="card" key={pkg.id}>
              <div className="card-header">
                <div><div className="card-title">{pkg.package_name}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pkg.package_code}</div></div>
                <Link to={`/packages/${pkg.id}`} className="btn btn-sm btn-secondary">View Package →</Link>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                <thead><tr style={{ background: 'var(--bg)' }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>GP From (LKR)</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>GP To (LKR)</th>
                  <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Commission (LKR)</th>
                </tr></thead>
                <tbody>{tiers.sort((a, b) => parseFloat(a.gp_from) - parseFloat(b.gp_from)).map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px' }}>≥ {parseFloat(t.gp_from).toLocaleString()}</td>
                    <td style={{ padding: '8px 14px' }}>{t.gp_to != null ? '< ' + parseFloat(t.gp_to).toLocaleString() : 'and above'}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>LKR {parseFloat(t.commission_amount).toLocaleString()}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))}
          {pkgTiers.length === 0 && <div className="empty-state"><div className="empty-state-icon">💰</div><div className="empty-state-title">No per-package commissions yet</div><div className="empty-state-subtitle">Set up from the Commission tab inside each package</div></div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {blocks.map(block => (
            <div className="card" key={block.rule_name}>
              <div className="card-header">
                <div style={{ flex: 1 }}>
                  {renamingBlock[block.rule_name] !== undefined ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input className="form-input" style={{ margin: 0, maxWidth: 280 }} autoFocus
                        value={renamingBlock[block.rule_name]}
                        onChange={e => setRenamingBlock(s => ({ ...s, [block.rule_name]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') saveBlockRename(block.rule_name, renamingBlock[block.rule_name]); if (e.key === 'Escape') setRenamingBlock(s => { const n = { ...s }; delete n[block.rule_name]; return n; }); }}
                      />
                      <button className="btn btn-primary btn-sm" onClick={() => saveBlockRename(block.rule_name, renamingBlock[block.rule_name])}>Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setRenamingBlock(s => { const n = { ...s }; delete n[block.rule_name]; return n; })}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="card-title" style={{ fontSize: 16 }}>{block.rule_name}</div>
                      <button onClick={() => setRenamingBlock(s => ({ ...s, [block.rule_name]: block.rule_name }))} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, padding: '2px 7px' }}>Rename</button>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {COMM_TYPES.find(c => c.value === block.commission_type)?.label || block.commission_type}
                    {block.notes ? ` — ${block.notes}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {!addingTier[block.rule_name] && (
                    <button className="btn btn-sm btn-secondary" onClick={() => setAddingTier(s => ({ ...s, [block.rule_name]: blankTier(block.commission_type) }))}>+ Add Tier</button>
                  )}
                  <button className="btn btn-sm" style={{ background: 'var(--danger)', color: '#fff' }} onClick={() => deleteBlock(block.rule_name)}>Delete Block</button>
                </div>
              </div>

              {/* Existing tiers */}
              {block.tiers.length > 0 && (
                <div style={{ marginTop: 10, marginBottom: addingTier[block.rule_name] ? 12 : 0 }}>
                  {block.tiers.map(tier => (
                    <div key={tier.id}>
                      {editingTier[tier.id] ? (
                        <div style={{ background: 'var(--bg)', borderRadius: 7, padding: 14, marginBottom: 8 }}>
                          {renderTierFields(block.commission_type, editingTier[tier.id],
                            (k, v) => setEditingTier(s => ({ ...s, [tier.id]: { ...s[tier.id], [k]: v } })))}
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingTier(s => { const n = { ...s }; delete n[tier.id]; return n; })}>Cancel</button>
                            <button className="btn btn-primary btn-sm" disabled={savingEditTier[tier.id]} onClick={() => saveEditTier(tier, block)}>
                              {savingEditTier[tier.id] ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                          <span style={{ color: 'var(--text)' }}>{tierDesc(tier, block.commission_type)}</span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => startEditTier(tier, block.commission_type)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: 'var(--primary)', fontSize: 12, padding: '2px 8px' }}>Edit</button>
                            <button onClick={() => deleteTier(tier.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Inline add tier form */}
              {addingTier[block.rule_name] && (
                <div style={{ background: 'var(--bg)', borderRadius: 7, padding: 14, marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase' }}>New Tier</div>
                  {renderTierFields(block.commission_type, addingTier[block.rule_name],
                    (k, v) => setAddingTier(s => ({ ...s, [block.rule_name]: { ...s[block.rule_name], [k]: v } })))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setAddingTier(s => { const n = { ...s }; delete n[block.rule_name]; return n; })}>Cancel</button>
                    <button className="btn btn-primary btn-sm" disabled={savingTier[block.rule_name]} onClick={() => saveTier(block)}>
                      {savingTier[block.rule_name] ? 'Saving…' : 'Save Tier'}
                    </button>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 4 }}>saves and stays open to add more</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          {blocks.length === 0 && !newBlock && (
            <div className="empty-state">
              <div className="empty-state-icon">💰</div>
              <div className="empty-state-title">No commission blocks for {tab}s yet</div>
              <div className="empty-state-subtitle">Click "+ New Commission Block" to create one</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
